import {
	requestApiDelete,
	requestApiGet,
	requestApiGetFile,
	requestApiPatch,
	requestApiPost,
} from "../api/BacklogApiEndpoint";
import type { BacklogConfig } from "../contracts/BacklogConfig";
import type { BacklogApiContext } from "../contracts/api/BacklogApiContext";
import type { BacklogResponse } from "../contracts/api/BacklogResponse";
import type { BacklogResponseFile } from "../contracts/api/BacklogResponseFile";
import type { PullRequest } from "../entities/PullRequest";
import type { PullRequestAttachmentFile } from "../entities/PullRequestAttachmentFile";
import type { PullRequestComment } from "../entities/PullRequestComment";
import type { PullRequestCommentCount } from "../entities/PullRequestCommentCount";
import type { PullRequestCount } from "../entities/PullRequestCount";
import type { Repository } from "../entities/Repository";
import { assert } from "../utils/BacklogUtil";
import type { AddPullRequestCommentParams } from "./params/AddPullRequestCommentParams";
import type { AddPullRequestParams } from "./params/AddPullRequestParams";
import type { DeletePullRequestAttachmentsParams } from "./params/DeletePullRequestAttachmentsParams";
import type { DownloadPullRequestAttachmentParams } from "./params/DownloadPullRequestAttachmentParams";
import type { GetGitRepositoryParams } from "./params/GetGitRepositoryParams";
import type { GetListOfGitRepositoriesParams } from "./params/GetListOfGitRepositoriesParams";
import type { GetListOfPullRequestAttachmentParams } from "./params/GetListOfPullRequestAttachmentParams";
import type { GetNumberOfPullRequestCommentsParams } from "./params/GetNumberOfPullRequestCommentsParams";
import type { GetNumberOfPullRequestsParams } from "./params/GetNumberOfPullRequestsParams";
import type { GetPullRequestCommentParams } from "./params/GetPullRequestCommentParams";
import type { GetPullRequestListParams } from "./params/GetPullRequestListParams";
import type { GetPullRequestParams } from "./params/GetPullRequestParams";
import type { UpdatePullRequestCommentInformationParams } from "./params/UpdatePullRequestCommentInformationParams";
import type { UpdatePullRequestParams } from "./params/UpdatePullRequestParams";

/**
 * Get List of Git Repositories Gitリポジトリ一覧の取得
 *
 * Returns list of Git repositories.
 */
export function getListOfGitRepositories(
	config: BacklogConfig,
	params: GetListOfGitRepositoriesParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Repository[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/git/repositories",
		params,
		context,
	});
}

/**
 * Get Git Repository Gitリポジトリの取得
 *
 * Returns Git repository.
 */
export function getGitRepository(
	config: BacklogConfig,
	params: GetGitRepositoryParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Repository>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName",
		params,
		context,
	});
}

/**
 * Get Pull Request List プルリクエスト一覧の取得
 *
 * Returns list of pull requests.
 */
export function getPullRequestList(
	config: BacklogConfig,
	params: GetPullRequestListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<PullRequest>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName/pullRequests",
		params,
		context,
	});
}

/**
 * Get Number of Pull Requests プルリクエスト数の取得
 *
 * Returns number of pull requests.
 */
export function getNumberOfPullRequests(
	config: BacklogConfig,
	params: GetNumberOfPullRequestsParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<PullRequestCount>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName/pullRequests/count",
		params,
		context,
	});
}

/**
 * Add Pull Request プルリクエストの追加
 *
 * Adds pull requests.
 */
export function addPullRequest(
	config: BacklogConfig,
	params: AddPullRequestParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<PullRequest>> {
	assert(params);
	return requestApiPost({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName/pullRequests",
		params,
		context,
	});
}

/**
 * Get Pull Request プルリクエストの取得
 *
 * Returns pull reuqest.
 */
export function getPullRequest(
	config: BacklogConfig,
	params: GetPullRequestParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<PullRequest>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName/pullRequests/:number",
		params,
		context,
	});
}

/**
 * Update Pull Request プルリクエストの追加
 *
 * Updates pull requests.
 */
export function updatePullRequest(
	config: BacklogConfig,
	params: UpdatePullRequestParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<PullRequest>> {
	assert(params);
	return requestApiPatch({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName/pullRequests/:number",
		params,
		context,
	});
}

/**
 * Get Pull Request Comment プルリクエストコメントの取得
 *
 * Returns list of pull request comments.
 */
export function getPullRequestComment(
	config: BacklogConfig,
	params: GetPullRequestCommentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<PullRequestComment>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName/pullRequests/:number/comments",
		params,
		context,
	});
}

/**
 * Add Pull Request Comment プルリクエストコメントの追加
 *
 * Adds comments on pull requests.
 */
export function addPullRequestComment(
	config: BacklogConfig,
	params: AddPullRequestCommentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<PullRequestComment>> {
	assert(params);
	return requestApiPost({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName/pullRequests/:number/comments",
		params,
		context,
	});
}

/**
 * Get Number of Pull Request Comments プルリクエストコメント数の取得
 *
 * Returns number of comments on pull requests.
 */
export function getNumberOfPullRequestComments(
	config: BacklogConfig,
	params: GetNumberOfPullRequestCommentsParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<PullRequestCommentCount>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName/pullRequests/:number/comments/count",
		params,
		context,
	});
}

/**
 * Update Pull Request Comment Information プルリクエストコメント情報の更新
 *
 * Updates pull request comment information.
 *
 * Authenticated user can update his own comments.
 */
export function updatePullRequestCommentInformation(
	config: BacklogConfig,
	params: UpdatePullRequestCommentInformationParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<PullRequestComment>> {
	assert(params);
	return requestApiPatch({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName/pullRequests/:number/comments/:commentId",
		params,
		context,
	});
}

/**
 * Get List of Pull Request Attachment プルリクエスト添付ファイル一覧の取得
 *
 * Returns list of attached files on pull requests.
 */
export function getListOfPullRequestAttachment(
	config: BacklogConfig,
	params: GetListOfPullRequestAttachmentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<PullRequestAttachmentFile[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName/pullRequests/:number/attachments",
		params,
		context,
	});
}

/**
 * Download Pull Request Attachment プルリクエスト添付ファイルのダウンロード
 *
 * Downloads attached files on pull requests.
 */
export function downloadPullRequestAttachment(
	config: BacklogConfig,
	params: DownloadPullRequestAttachmentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<BacklogResponseFile>> {
	assert(params);
	return requestApiGetFile({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName/pullRequests/:number/attachments/:attachmentId",
		params,
		context,
	});
}

/**
 * Delete Pull Request Attachments プルリクエスト添付ファイルの削除
 *
 * Deletes attached files on pull requests.
 */
export function deletePullRequestAttachments(
	config: BacklogConfig,
	params: DeletePullRequestAttachmentsParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<PullRequestAttachmentFile>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "projects/:projectIdOrKey/git/repositories/:repoIdOrName/pullRequests/:number/attachments/:attachmentId",
		params,
		context,
	});
}
